import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AchievementIcon } from '../../components/AchievementIcon';
import { useBlocker, useNavigate, useParams } from 'react-router';
import { AppError, uuidv7 } from '@st/contract';
import { useLogWorkout, useTrialStatus, useWorkout } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { LockedWorkout } from '../../components/LockedWorkout';
import { PageHeader } from '../../components/PageHeader';
import { ExerciseThumb } from '../../ui/ExerciseThumb';
import { useTranslation } from '../../i18n/I18nProvider';
import { toErrorMessage } from '../../i18n/errorMessage';
import { formatNumber } from '../../lib/format';
import {
  findPersonalRecords,
  formatDuration,
  totalVolumeKg,
  type ExerciseLog,
  type PersonalRecord,
  type PreviousRecords,
} from '../../lib/workout';
import type { UnlockedAchievement, WorkoutDetail } from '../../api/types';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  ErrorState,
  RestTimer,
  SetRow,
  SkeletonCard,
  StatTile,
  useToast,
  type SetRowValue,
} from '../../ui';

type SessionState = Record<string, SetRowValue[]>;

/**
 * Zadani broj setova kad ih trener nije propisao. API `sets` vraća kao
 * nullable — bez ovoga bi ekran nacrtao nula redaka i trening se ne bi mogao
 * upisati.
 */
const DEFAULT_SETS = 3;

/** Odmor kad trener ne propiše svoj — ista vrijednost kao u builderu. */
const DEFAULT_REST_SECONDS = 60;

/**
 * Broj iz propisa („45 s", „10-12") kao pocetna vrijednost polja za trajanje.
 * Kod raspona uzima donju granicu — nije cilj pogoditi nego ne tjerati na
 * tipkanje ocitog.
 */
function hintSeconds(reps: string | null): number | '' {
  if (!reps) return '';
  const m = /\d+/.exec(reps);
  return m ? Number(m[0]) : '';
}

function initialState(workout: WorkoutDetail): SessionState {
  const out: SessionState = {};
  for (const ex of workout.exercises) {
    // Kilaza se predpuni samo tamo gdje kilaza uopce postoji.
    const weightKg = ex.loadType === 'weighted' ? (ex.weightHintKg ?? ('' as const)) : ('' as const);
    const durationSeconds = ex.loadType === 'timed' ? hintSeconds(ex.reps) : ('' as const);
    out[ex.workoutExerciseId] = Array.from({ length: Math.max(1, ex.sets ?? DEFAULT_SETS) }, () => ({
      reps: '' as const,
      weightKg,
      durationSeconds,
      done: false,
    }));
  }
  return out;
}

export function WorkoutSession() {
  const { id = '' } = useParams();
  const query = useWorkout(id);
  const trial = useTrialStatus();
  const { t } = useTranslation();

  /**
   * 402 nije greška ekrana nego stanje pristupa: trening iza probnog pristupa
   * vodi na isti poziv na kupnju kao i `reason: 'locked'` na treningu dana.
   */
  if (query.isError && query.error instanceof AppError && query.error.code === 'payment_required') {
    return (
      <>
        <PageHeader eyebrow={t('training.title')} title={t('trial.locked')} />
        <LockedWorkout
          name={trial.data?.lockedFrom?.name ?? null}
          trialCount={trial.data?.trial?.total ?? null}
        />
      </>
    );
  }

  return (
    <QueryBoundary query={query} skeleton={<SkeletonCard />}>
      {(workout) => (
        <Session key={workout.workoutId} workout={workout} title={t('training.title')} />
      )}
    </QueryBoundary>
  );
}

function Session({ workout, title }: { workout: WorkoutDetail; title: string }) {
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const logWorkout = useLogWorkout();

  const startedAt = useRef(new Date().toISOString());
  const [state, setState] = useState<SessionState>(() => initialState(workout));
  const [restFor, setRestFor] = useState<{ exerciseId: string; seconds: number } | null>(null);
  const [summary, setSummary] = useState<{
    volume: number;
    records: PersonalRecord[];
    seconds: number;
    /** Server šalje samo novootključana postignuća, pa se čestitka pokazuje jednom. */
    achievements: UnlockedAchievement[];
  } | null>(null);

  const exerciseLogs: ExerciseLog[] = useMemo(
    () =>
      workout.exercises.map((ex) => ({
        exerciseId: ex.exerciseId,
        exerciseName: ex.name,
        sets: (state[ex.workoutExerciseId] ?? []).map((s, i) => ({
          setIndex: i,
          reps: s.reps === '' ? null : s.reps,
          weightKg: s.weightKg === '' ? null : s.weightKg,
          durationSeconds:
            s.durationSeconds === '' || s.durationSeconds == null ? null : s.durationSeconds,
          done: s.done,
        })),
      })),
    [workout.exercises, state],
  );

  const runningVolume = totalVolumeKg(exerciseLogs);

  /**
   * Sve dok trening nije poslan, upisani setovi žive samo u stanju ekrana.
   * Donja navigacija je na telefonu jedan promašeni dodir daleko, a odlazak s
   * ekrana je do sada tiho pojeo cijeli trening. Zato: pitanje prije odlaska
   * (unutar portala) i preglednikovo upozorenje pri zatvaranju kartice.
   */
  /*
    Usporedba ide prema POČETNOM stanju, ne prema „ima li ijedna vrijednost":
    kilaža se predpuni iz `weightHintKg`, pa bi svaki takav trening odmah
    izgledao kao da ima nespremljen unos i pitao bi pri svakom izlasku.
  */
  const pristine = useMemo(() => JSON.stringify(initialState(workout)), [workout]);
  const hasUnsaved = summary === null && JSON.stringify(state) !== pristine;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsaved && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!hasUnsaved) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsaved]);

  const previous: PreviousRecords = useMemo(() => {
    const map: PreviousRecords = {};
    for (const ex of workout.exercises) {
      // API vraća ZADNJI učinak po vježbi (`lastPerformance`), ne najbolji.
      // Volumen zato računamo iz njega; prazna polja ne ulaze u usporedbu.
      const last = ex.lastPerformance;
      if (last && last.weightKg !== null && last.reps !== null) {
        map[ex.exerciseId] = {
          weight: last.weightKg,
          reps: last.reps,
          volume: last.weightKg * last.reps,
        };
      }
    }
    return map;
  }, [workout.exercises]);

  const updateSet = useCallback(
    (exerciseId: string, index: number, next: SetRowValue, restSeconds: number) => {
      setState((prev) => {
        const rows = prev[exerciseId] ?? [];
        const copy = [...rows];
        const before = copy[index];
        copy[index] = next;
        if (before && !before.done && next.done) {
          setRestFor({ exerciseId, seconds: restSeconds });
        }
        return { ...prev, [exerciseId]: copy };
      });
    },
    [],
  );

  const addSet = useCallback((exerciseId: string) => {
    setState((prev) => {
      const rows = prev[exerciseId] ?? [];
      const last = rows.at(-1);
      return {
        ...prev,
        [exerciseId]: [
          ...rows,
          {
            reps: '' as const,
            weightKg: last?.weightKg ?? ('' as const),
            durationSeconds: last?.durationSeconds ?? ('' as const),
            done: false,
          },
        ],
      };
    });
  }, []);

  async function finish() {
    const records = findPersonalRecords(exerciseLogs, previous);
    const seconds = Math.round((Date.now() - new Date(startedAt.current).getTime()) / 1000);
    const completedAt = new Date().toISOString();

    const sets = exerciseLogs.flatMap((ex) =>
      ex.sets
        .filter((s) => s.done)
        .map((s) => ({
          clientIdempotencyKey: uuidv7(),
          exerciseId: ex.exerciseId,
          setIndex: s.setIndex,
          reps: s.reps,
          weightKg: s.weightKg,
          durationSeconds: s.durationSeconds ?? null,
          completedAt,
        })),
    );

    try {
      const result = await logWorkout.mutateAsync({
        clientIdempotencyKey: uuidv7(),
        workoutId: workout.workoutId,
        startedAt: startedAt.current,
        completedAt,
        sets,
      });
      setSummary({
        volume: result.totalVolumeKg || totalVolumeKg(exerciseLogs),
        records,
        seconds: result.durationSeconds || seconds,
        achievements: result.achievements,
      });
    } catch (err) {
      // Zapisi ostaju u stanju ekrana — ARHITEKTURA.md sekcija 9: ništa se ne gubi.
      toast(toErrorMessage(t, err).title, 'danger');
    }
  }

  if (summary) {
    return (
      <>
        <PageHeader eyebrow={title} title={t('training.finished')} />
        <div className="grid gap-4 sm:grid-cols-2">
          <StatTile
            label={t('training.totalVolume')}
            value={formatNumber(summary.volume, locale)}
            unit="kg"
          />
          <StatTile label={t('training.duration')} value={formatDuration(summary.seconds)} />
        </div>
        {summary.achievements.length > 0 && (
          <Card className="mt-4" data-testid="workout-achievements">
            <CardHeader title={t('achievements.unlockedTitle')} />
            <p className="mb-4 text-[14px] leading-relaxed text-ink-muted">
              {t('achievements.unlockedHelp')}
            </p>
            <ul className="flex flex-wrap gap-3">
              {summary.achievements.map((a) => (
                <li
                  key={a.key}
                  className="flex items-center gap-2 rounded-pill bg-accent-light px-4 py-2 text-[14px] text-accent-deep"
                >
                  <AchievementIcon name={a.icon} size={18} />
                  {a.label}
                </li>
              ))}
            </ul>
          </Card>
        )}

        <Card className="mt-4">
          <CardHeader title={t('training.newRecords')} />
          {summary.records.length === 0 ? (
            <p className="text-[14.5px] text-ink-muted">{t('training.noRecords')}</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {summary.records.map((r) => (
                <li
                  key={`${r.exerciseId}-${r.metric}`}
                  className="flex items-center justify-between gap-3 border-b border-line-subtle pb-2 last:border-b-0"
                >
                  <span className="text-[14.5px] text-ink">{r.exerciseName}</span>
                  <Badge tone="accent">
                    {t(`training.pr${r.metric[0]?.toUpperCase()}${r.metric.slice(1)}`)}:{' '}
                    {formatNumber(r.value, locale, r.metric === 'reps' ? 0 : 1)}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Button className="mt-6" onClick={() => void navigate('/')}>
          {t('training.backToDashboard')}
        </Button>
      </>
    );
  }

  const leaveDialog = (
    <ConfirmDialog
      open={blocker.state === 'blocked'}
      tone="neutral"
      title={t('training.leaveTitle')}
      question={t('training.leaveQuestion')}
      confirmLabel={t('training.leaveConfirm')}
      cancelLabel={t('training.leaveStay')}
      closeLabel={t('common.close')}
      onConfirm={() => blocker.proceed?.()}
      onClose={() => blocker.reset?.()}
    />
  );

  const anyDone = exerciseLogs.some((ex) => ex.sets.some((s) => s.done));

  return (
    <>
      <PageHeader
        eyebrow={title}
        title={workout.name}
        subtitle={t('training.totalVolume') + ': ' + formatNumber(runningVolume, locale) + ' kg'}
      />

      {logWorkout.isError && (
        <ErrorState
          className="mb-4"
          title={toErrorMessage(t, logWorkout.error).title}
          help={toErrorMessage(t, logWorkout.error).help}
          retryLabel={t('common.retry')}
          onRetry={() => void finish()}
        />
      )}

      <div className="flex flex-col gap-5">
        {workout.exercises.map((ex) => (
          <Card key={ex.workoutExerciseId} as="section">
            <CardHeader
              title={
                <span className="flex items-center gap-3">
                  {/*
                    Sličica pomaže prije nego što se pročita ime — kod vježbe
                    koju klijent radi prvi put slika kaže više od naziva.
                  */}
                  <ExerciseThumb src={ex.imageUrl} name={ex.name} size="md" />
                  <span className="min-w-0">{ex.name}</span>
                </span>
              }
              action={
                <span className="text-[13px] text-ink-muted">
                  {ex.sets ?? DEFAULT_SETS}×{ex.reps ?? '—'}
                  {ex.tempo ? ` · ${t('training.tempo')} ${ex.tempo}` : ''}
                </span>
              }
            />
            {ex.trainerNote && (
              <p className="mb-4 rounded-md bg-bg-alt px-4 py-3 text-[13px] leading-relaxed text-ink-muted">
                <span className="eyebrow mr-2">{t('training.trainerNote')}</span>
                {ex.trainerNote}
              </p>
            )}

            <div className="flex flex-col gap-2">
              {(state[ex.workoutExerciseId] ?? []).map((row, i) => (
                <SetRow
                  key={i}
                  index={i}
                  value={row}
                  showHeader={i === 0}
                  loadType={ex.loadType}
                  targetHint={ex.reps ?? ''}
                  onChange={(next) =>
                    updateSet(ex.workoutExerciseId, i, next, ex.restSeconds ?? DEFAULT_REST_SECONDS)
                  }
                  labels={{
                    set: t('training.setN', { n: '' }).trim(),
                    reps: t('training.reps'),
                    weight: t('training.weightKg'),
                    addedWeight: t('training.addedWeightKg'),
                    addWeight: t('training.addWeight'),
                    duration: t('training.durationSeconds'),
                    markDone: t('training.markDone'),
                    invalidReps: t('training.invalidReps'),
                    invalidWeight: t('training.invalidWeight'),
                    invalidDuration: t('training.invalidDuration'),
                  }}
                />
              ))}
            </div>

            <Button
              variant="ghost"
              size="sm"
              className="mt-3"
              onClick={() => addSet(ex.workoutExerciseId)}
            >
              + {t('training.addSet')}
            </Button>

            {restFor?.exerciseId === ex.workoutExerciseId && (
              <RestTimer
                className="mt-4"
                seconds={restFor.seconds}
                autoStart
                onSkip={() => setRestFor(null)}
                onComplete={() => setRestFor(null)}
                labels={{
                  title: t('training.restTimer'),
                  start: t('training.start'),
                  pause: t('training.pause'),
                  resume: t('training.resume'),
                  skip: t('training.skip'),
                  done: t('training.restDone'),
                }}
              />
            )}
          </Card>
        ))}
      </div>

      <div className="sticky bottom-20 mt-6 md:bottom-4">
        {/*
          Ugašen gumb bez objašnjenja je slijepa ulica: klijent klikne „Završi”,
          ništa se ne dogodi i ne piše zašto. Rečenica ispod kaže što nedostaje i
          vezana je uz gumb preko `aria-describedby`.
        */}
        <Button
          block
          size="lg"
          disabled={!anyDone}
          aria-describedby={anyDone ? undefined : 'finish-hint'}
          loading={logWorkout.isPending}
          onClick={() => void finish()}
        >
          {t('training.finish')}
        </Button>
        {!anyDone && (
          <p
            id="finish-hint"
            className="mt-2 rounded-md bg-bg/90 px-3 py-2 text-center text-[12.5px] text-ink-muted backdrop-blur"
          >
            {t('training.finishNeedsSet')}
          </p>
        )}
      </div>

      {leaveDialog}
    </>
  );
}
